import styled, { css } from 'styled-components';

/**
 * The topbar from the design system.
 *
 * It carries the page title — screens no longer draw a heading of their own —
 * plus the back button, the notification bell and the user chip. The
 * decorative search icon that sat here is gone; it never did anything.
 */

export const HeaderContainer = styled.header`
  display: flex;
  align-items: center;
  gap: var(--s3);
  height: var(--h-topbar);
  flex: 0 0 var(--h-topbar);
  padding: 0 var(--s5);
  background: var(--surface);
  border-bottom: 1px solid var(--line);
`;

/** The one control shape in the bar: square, quiet, ground on hover. */
const iconAffordance = css`
  display: grid;
  place-items: center;
  flex: 0 0 var(--h-btn-sm);
  width: var(--h-btn-sm);
  height: var(--h-btn-sm);
  padding: 0;
  border: none;
  border-radius: var(--r-sm);
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  position: relative;
  transition:
    background-color 0.12s,
    color 0.12s;

  &:hover {
    background: var(--surface-alt);
    color: var(--text);
  }

  svg {
    width: 17px;
    height: 17px;
    stroke-width: 1.7;
  }

  /* The bell and the back arrow paint themselves with a presentation
     attribute; CSS outranks it, so both follow the button's colour. */
  svg.injected-svg[fill] {
    fill: currentColor;
  }

  svg.injected-svg[stroke] {
    stroke: currentColor;
  }

  &.newNotif::after {
    content: '';
    position: absolute;
    top: var(--s1);
    right: var(--s1);
    width: 6px;
    height: 6px;
    border-radius: var(--r-pill);
    background: var(--danger);
    border: 1.5px solid var(--surface);
  }
`;

export const IconButton = styled.button`
  ${iconAffordance}
`;

/**
 * The same shape as a div. The bell sits inside MiniPopUp, which supplies the
 * <button> itself, and a button inside a button is not valid HTML.
 */
export const IconBox = styled.div`
  ${iconAffordance}
`;

export const Title = styled.h1`
  font-size: var(--fs-title);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-body);
  letter-spacing: -0.01em;
  color: var(--text);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Which corner of the product the screen belongs to. */
export const Meta = styled.span`
  flex: 0 0 auto;
  padding: 2px var(--s2);
  background: var(--surface-alt);
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  font-family: var(--font-mono);
  font-size: var(--fs-micro);
  line-height: var(--lh-body);
  color: var(--text-faint);
  white-space: nowrap;
`;

export const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
  margin-left: auto;
`;

export const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
  padding: var(--s1) var(--s2) var(--s1) var(--s1);
  border-radius: var(--r-pill);
  cursor: pointer;
  transition: background-color 0.12s;

  &:hover {
    background: var(--surface-alt);
  }

  svg {
    width: 13px;
    height: 13px;
    color: var(--text-faint);
  }
`;

export const Image = styled.img`
  width: var(--h-avatar);
  height: var(--h-avatar);
  flex: 0 0 var(--h-avatar);
  border-radius: var(--r-pill);
  object-fit: cover;
`;

export const ProfileTitle = styled.span`
  font-size: var(--fs-label);
  font-weight: var(--fw-regular);
  line-height: var(--lh-body);
  color: var(--text-muted);
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Ul = styled.ul`
  list-style: none;
`;

export const Li = styled.li`
  a {
    display: block;
    padding: var(--s1) 0;
    font-size: var(--fs-body);
    color: var(--text);
  }

  a:hover {
    color: var(--primary);
  }
`;

export const Notifications = styled.div``;

export const Grid = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s2);
`;

export const NotifTitle = styled.h4`
  font-size: var(--fs-th);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-body);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--text-faint);
  margin: 0;
`;

export const NotifSpan = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px var(--s2);
  margin-left: auto;
  border-radius: var(--r-pill);
  background: var(--primary-soft);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
  color: var(--primary);
`;

export const NotifList = styled.ul`
  list-style: none;
  max-height: 400px;
  overflow-y: auto;
`;

export const NotifItem = styled.li`
  display: block;
  padding: var(--s3) var(--s4);
  border-bottom: 1px solid var(--line);

  &:last-child {
    border-bottom: none;
  }

  &.unRead {
    background: var(--primary-soft);
  }
`;

export const NotifItemTitle = styled.h5`
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-body);
  color: var(--text);
  margin: 0;
`;

export const NotifItemText = styled.p`
  font-size: var(--fs-label);
  font-weight: var(--fw-regular);
  line-height: var(--lh-body);
  color: var(--text-muted);
`;

export const NotifDate = styled.span`
  display: block;
  margin-top: var(--s1);
  font-family: var(--font-mono);
  font-size: var(--fs-micro);
  color: var(--text-faint);
`;
